// client/src/pages/admin/AdminLogin.jsx
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings
} from '@mui/icons-material';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    // Make API call to the backend login endpoint
    const response = await fetch('https://propertyearn.net/api/api/admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: credentials.username,
        password: credentials.password
      }),
    });

    // Parse the JSON response
    const data = await response.json();

    // Check if the response was successful
    if (!response.ok) {
      // If the server returned an error message, use it
      throw new Error(data.error || `Login failed with status: ${response.status}`);
    }

    // Login was successful
    // 1. Store the received JWT token
    localStorage.setItem('adminToken', data.access_token);
    
    // Optional: Store admin user info if needed elsewhere
    localStorage.setItem('adminUser', JSON.stringify(data.admin));
    
    // 2. Redirect to the admin dashboard
    navigate('/admin/dashboard');

  } catch (err) {
    // Handle any errors (network errors, JSON parsing errors, etc.)
    setError(err.message);
  } finally {
    setLoading(false);
  }
};


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        elevation={8}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {/* Logo / Title Area */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <AdminPanelSettings color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" component="h1" gutterBottom fontWeight="700">
            PropertyEarn
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Administrator Portal
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <TextField
          required
          fullWidth
          label="Admin Username"
          name="username"
          value={credentials.username}
          onChange={handleChange}
          disabled={loading}
          autoComplete="username"
        />
        <TextField
          required
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={credentials.password}
          onChange={handleChange}
          disabled={loading}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Authorized personnel only. All activities are logged.
        </Typography>
      </Paper>
    </Box>
  );
}